import { Box, Flex, StackProps, useBoolean } from "@biblioteksentralen/react";
import { HoldingText } from "../HoldingText";
import { ManifestationStatusForUser } from "../manifestations/ManifestationStatusForUser";
import { FindInLibraryModal } from "../works/FindInLibraryModal";
import { ReservationButton } from "./ReservationButton";
import { ReservationContainer, reservationFullWidthBreakpoint } from "./ReservationContainer";
import { ReservationLanguageMenu } from "./ReservationLanguageMenu";
import { ReservationVariantButtons } from "./ReservationVariantButtons";
import { formatLanguagesList, languageListsEqual } from "@libry-content/integrations";
import { useWorkViewContext } from "../works/WorkViewContextProvider";
import FindInLibraryButton from "../works/FindInLibraryButton";

export const Reservation = (stackProps: StackProps) => {
  const [findInLibraryModalOpen, { on: openFindInLibraryModal, off: closeFindInLibraryModal }] = useBoolean();

  const { work, holdingsData, currentVariant, manifestationStatusForUser, libraries } = useWorkViewContext();
  const representativeWorkTriple = work?.representativeWorkTriple;

  if (!work || !representativeWorkTriple) return null;

  const variants = work.getVariantsWithTriples();
  const currentLanguagesList = currentVariant?.variant.languages;
  const currentDocumentType = currentVariant?.variant.documentType;

  const someLanguageLabelsAreLong = variants.some(
    ({ variant }) => (formatLanguagesList(variant.languages ?? [])?.length ?? 0) > 10
  );

  // Use language menu if there is more than three options total
  // or if the langauge labels are more than + characters and there are more than one option total

  const useLanguageMenu = !!(variants.length > 3 || (someLanguageLabelsAreLong && variants.length > 1));

  return (
    <ReservationContainer documentType={currentVariant?.variant.documentType} {...stackProps}>
      <Box width="100%" position="relative">
        <form aria-label="form">
          {useLanguageMenu && (work?.isInteractive ? work.getLanguages().length > 1 : true) && (
            <ReservationLanguageMenu languages={work?.getLanguages()} />
          )}
          {variants && variants.length > 0 && (
            <ReservationVariantButtons
              useLanguageMenu={useLanguageMenu}
              variants={(useLanguageMenu
                ? variants.filter(({ variant }) => languageListsEqual(variant.languages, currentLanguagesList))
                : variants
              ).map(({ variant }) => variant)}
              marginBottom={{ base: "1rem", [reservationFullWidthBreakpoint]: "1.25rem" }}
            />
          )}
        </form>
        <HoldingText
          work={work}
          workTriples={currentVariant?.workTriples}
          holdings={holdingsData?.holdings}
          manifestationStatusForUser={manifestationStatusForUser}
          marginBottom="0.5rem"
          justifyContent="center"
          alignItems="center"
          textProps={{ fontWeight: "normal", fontSize: "md" }}
          separateLines
        />
        <ManifestationStatusForUser
          manifestationStatusForUser={manifestationStatusForUser}
          documentType={currentDocumentType}
          marginBottom="0.5rem"
        />
        {manifestationStatusForUser && (
          <ReservationButton
            representativeWorkTriple={representativeWorkTriple}
            variantWithTriples={currentVariant}
            manifestationStatusForUser={manifestationStatusForUser}
            height="3rem"
          />
        )}
        <Flex justifyContent="center" marginTop="0.75rem">
          <FindInLibraryButton
            variant="secondary"
            width="100%"
            height="3rem"
            fontSize="lg"
            openFindInLibraryModal={openFindInLibraryModal}
          />
        </Flex>
      </Box>
      <FindInLibraryModal
        isOpen={findInLibraryModalOpen}
        onClose={closeFindInLibraryModal}
        representativeWorkTriple={representativeWorkTriple}
        workTriples={currentVariant?.workTriples}
        work={work}
        libraries={libraries}
        isRepresentedWork
      />
    </ReservationContainer>
  );
};
